export const PLAY_VERSION_CODE = 12;
export const PLAY_VERSION_NAME = '1.1.9';

export const NATIVE_VERSION_QUERY = 'zv';
export const NATIVE_VERSION_STORAGE_KEY = 'zeedcom_app_release';

export type AppRelease = {
  code: number;
  name: string;
};

export const defaultAppRelease = (): AppRelease => ({
  code: PLAY_VERSION_CODE,
  name: PLAY_VERSION_NAME
});

export const formatAppRelease = (release: AppRelease): string => (
  `(${release.code} : ${release.name})`
);

export const parseAppRelease = (raw: string | null | undefined): AppRelease | null => {
  if (!raw) return null;
  const match = raw.trim().match(/^(\d+)\s*[-:]\s*(.+)$/);
  if (!match) return null;
  const code = Number(match[1]);
  const name = match[2].trim();
  if (!Number.isInteger(code) || code < 1 || !name) return null;
  return { code, name };
};

export const takeAppReleaseFromSearch = (search: string): {
  release: AppRelease | null;
  nextSearch: string;
} => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const release = parseAppRelease(params.get(NATIVE_VERSION_QUERY));
  if (!release) {
    return { release: null, nextSearch: search };
  }
  params.delete(NATIVE_VERSION_QUERY);
  const remaining = params.toString();
  return {
    release,
    nextSearch: remaining ? `?${remaining}` : ''
  };
};

export const persistAppRelease = (release: AppRelease) => {
  const value = `${release.code}-${release.name}`;
  try {
    sessionStorage.setItem(NATIVE_VERSION_STORAGE_KEY, value);
    localStorage.setItem(NATIVE_VERSION_STORAGE_KEY, value);
  } catch {
    // Private mode can block storage.
  }
};

export const readStoredAppRelease = (): AppRelease | null => {
  try {
    return parseAppRelease(
      sessionStorage.getItem(NATIVE_VERSION_STORAGE_KEY)
      || localStorage.getItem(NATIVE_VERSION_STORAGE_KEY)
    );
  } catch {
    return null;
  }
};

export const readAppRelease = (): AppRelease => (
  readStoredAppRelease() || defaultAppRelease()
);

export const captureAppReleaseFromLocation = (): AppRelease => {
  if (typeof window === 'undefined') return defaultAppRelease();
  const taken = takeAppReleaseFromSearch(window.location.search);
  if (taken.release) {
    persistAppRelease(taken.release);
    const next = `${window.location.pathname}${taken.nextSearch}${window.location.hash}`;
    window.history.replaceState(null, '', next);
    return taken.release;
  }
  return readAppRelease();
};
